import { useState } from 'react';
import { Board } from '../board/Board';
import { checkAttempt } from '../../lib/exercises/checkAttempt';
import { normalizeUci } from '../../lib/utils/chessHelpers';
import { runForeground } from '../../lib/engine/engineGate';
import type { StockfishEngine } from '../../lib/engine/StockfishEngine';
import type { AttemptOutcome, Color, Exercise } from '../../types';

export interface ExercisePlayerProps {
  exercise: Exercise;
  engine: StockfishEngine;
  onCompleted: (outcome: AttemptOutcome, hintsUsed: number, playedUci?: string) => void;
}

type Phase = 'blunder-check' | 'solving' | 'checking' | 'solved' | 'revealed';

function opponentOf(color: Color): Color {
  return color === 'w' ? 'b' : 'w';
}

export function ExercisePlayer({ exercise, engine, onCompleted }: ExercisePlayerProps) {
  const hasMistake = Boolean(exercise.mistake);
  // Étape préalable optionnelle : « qu'est-ce que ce coup menaçait ? »,
  // avant de chercher le meilleur coup. N'affecte jamais la révision
  // espacée (aucun appel à onCompleted tant qu'on n'a pas quitté cette
  // phase) — seule l'issue de la phase `solving` compte, comme avant.
  const [phase, setPhase] = useState<Phase>(hasMistake ? 'blunder-check' : 'solving');
  const [threatMessage, setThreatMessage] = useState<string | null>(null);
  const [threatCleared, setThreatCleared] = useState(false);

  const [hintsUsed, setHintsUsed] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [lastPlayedUci, setLastPlayedUci] = useState<string | undefined>(undefined);

  function handleThreatGuess(uci: string) {
    if (!exercise.mistake) return;
    const { punishment } = exercise.mistake;
    const isReply = normalizeUci(uci) === normalizeUci(punishment.replyUci);
    const isCaptureOnThreatenedSquare = punishment.captured && uci.slice(2, 4) === punishment.captured.square;
    if (isReply || isCaptureOnThreatenedSquare) {
      setThreatMessage(`Bien vu : ${punishment.replySan} punit ce coup.`);
      setThreatCleared(true);
    } else {
      setThreatMessage('Pas tout à fait — cherche encore, ou passe à la suite.');
    }
  }

  function revealThreat() {
    if (!exercise.mistake) return;
    setThreatMessage(`La réponse était ${exercise.mistake.punishment.replySan}.`);
    setThreatCleared(true);
  }

  function proceedToSolving() {
    setPhase('solving');
    setThreatMessage(null);
    setThreatCleared(false);
  }

  async function handleMove(uci: string) {
    setPhase('checking');
    setLastPlayedUci(uci);
    try {
      const result = await runForeground(() => checkAttempt(exercise, uci, engine));
      if (!result.legal) {
        setMessage(result.error ?? 'Coup illégal.');
        setPhase('solving');
        return;
      }
      setAttempts((a) => a + 1);
      if (result.isGoodEnough) {
        const outcome: AttemptOutcome = hintsUsed > 0 ? 'hinted_success' : 'autonomous_success';
        setMessage(
          result.isGoalMove
            ? `Exactement le coup recherché : ${result.san} !`
            : `${result.san} est une alternative suffisamment bonne (perte ≈ ${((result.lossCp ?? 0) / 100).toFixed(1)} pion).`,
        );
        setPhase('solved');
        onCompleted(outcome, hintsUsed, uci);
      } else {
        setMessage(
          `${result.san} ne semble pas assez précis ici (perte ≈ ${((result.lossCp ?? 0) / 100).toFixed(1)} pion). Réessaie.`,
        );
        setPhase('solving');
      }
    } catch {
      setMessage("Le moteur n'a pas pu vérifier ce coup (analyse annulée ou en erreur). Réessaie.");
      setPhase('solving');
    }
  }

  function useHint() {
    if (hintsUsed >= exercise.hints.length) return;
    setHintsUsed((h) => h + 1);
  }

  function reveal() {
    setPhase('revealed');
    onCompleted('revealed', hintsUsed, lastPlayedUci);
  }

  function retry() {
    setPhase('solving');
    setMessage(null);
  }

  if (phase === 'blunder-check' && exercise.mistake) {
    return (
      <div className="exercise-player">
        <p className="muted">
          Dans ta partie, tu as joué <strong>{exercise.mistake.playedSan}</strong>. Quelle réponse adverse punit ce
          coup ?
        </p>
        <Board
          fen={exercise.mistake.punishment.fenAfterPlayed}
          orientation={exercise.playerColor}
          playableColor={threatCleared ? undefined : opponentOf(exercise.playerColor)}
          onUserMove={threatCleared ? undefined : handleThreatGuess}
          boardId={`threat-${exercise.id}`}
        />
        {threatMessage && (
          <p className={`feedback ${threatCleared ? 'success' : ''}`} role="status">
            {threatMessage}
          </p>
        )}
        <div className="actions">
          {!threatCleared && (
            <button type="button" className="link-btn" onClick={revealThreat}>
              Je ne vois pas
            </button>
          )}
          {threatCleared && (
            <button type="button" className="primary-btn" onClick={proceedToSolving}>
              Maintenant, trouve mieux
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="exercise-player">
      <p className="muted">
        {exercise.playerColor === 'w' ? 'Trait aux Blancs' : 'Trait aux Noirs'} — à toi de trouver le meilleur coup.
      </p>
      <Board
        fen={exercise.fen}
        orientation={exercise.playerColor}
        playableColor={phase === 'solving' ? exercise.playerColor : undefined}
        onUserMove={phase === 'solving' ? handleMove : undefined}
        boardId={`exercise-${exercise.id}`}
      />

      {phase === 'checking' && <p role="status">Vérification du coup…</p>}
      {message && phase !== 'checking' && (
        <p className={`feedback ${phase === 'solved' ? 'success' : ''}`} role="status">
          {message}
        </p>
      )}

      {hintsUsed > 0 && phase !== 'revealed' && (
        <ul className="hints-list">
          {exercise.hints.slice(0, hintsUsed).map((h, i) => (
            <li key={i}>{h}</li>
          ))}
        </ul>
      )}

      {phase === 'revealed' && (
        <div className="explanation">
          <p>
            Solution : <strong>{exercise.solutionSan}</strong>
            {exercise.solutionLineSan.length > 1 && ` (variante : ${exercise.solutionLineSan.slice(0, 4).join(' ')}…)`}
          </p>
        </div>
      )}

      {phase !== 'solved' && phase !== 'revealed' && (
        <div className="actions">
          {hintsUsed < exercise.hints.length && (
            <button type="button" className="link-btn" onClick={useHint}>
              Indice ({hintsUsed}/{exercise.hints.length})
            </button>
          )}
          <button type="button" className="link-btn" onClick={reveal}>
            Voir la solution
          </button>
          {attempts > 0 && (
            <button type="button" className="link-btn" onClick={retry}>
              Réessayer
            </button>
          )}
        </div>
      )}
    </div>
  );
}
